import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Handshake, Plus, Send, Clock, FileText, Files, Settings2, UserPlus, Users as UsersIcon, ExternalLink, Archive, ArchiveRestore, Trash2, Eye, EyeOff } from "lucide-react";

const STATUS_META = {
  draft:            { label: "Draft",             tone: "bg-[#F8F9FA] text-[#6C757D] border-[#E9ECEF]" },
  sent:             { label: "Sent to dealer",    tone: "bg-blue-50 text-blue-700 border-blue-200" },
  dealer_signed:    { label: "Awaiting counter",  tone: "bg-amber-50 text-amber-800 border-amber-200" },
  executed:         { label: "Executed",          tone: "bg-green-50 text-green-700 border-green-200" },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function DealersList() {
  const nav = useNavigate();
  const [dealers, setDealers] = useState([]);
  const [forms, setForms] = useState([]);
  const [role, setRole] = useState("agent");
  const [me, setMe] = useState(null);
  const [agencyUsers, setAgencyUsers] = useState([]);
  const [newDealer, setNewDealer] = useState({ dealer_name: "", contact_name: "", contact_email: "", contact_phone: "" });
  const [creating, setCreating] = useState(false);
  const [sendDialog, setSendDialog] = useState({ open: false, dealer: null, selected: new Set(), sending: false, providerFilter: "all" });
  const [assignDialog, setAssignDialog] = useState({ open: false, dealer: null, selected: new Set(), saving: false });
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const [dRes, fRes, meRes] = await Promise.all([
        api.get("/dealers", { params: { include_archived: showArchived } }),
        api.get("/dealer-forms").catch(() => ({ data: [] })),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      setDealers(dRes.data || []);
      setForms(fRes?.data || []);
      setRole(meRes?.data?.role || "agent");
      setMe(meRes?.data || null);
      // Fetch agency users for the assign dialog (only if admin/agency_admin)
      if (meRes?.data && (meRes.data.role === "admin" || meRes.data.role === "agency_admin") && meRes.data.agency_id) {
        try {
          const { data } = await api.get(`/agency/${meRes.data.agency_id}/users`);
          setAgencyUsers((data?.users || []).filter((u) => u.role !== "admin"));
        } catch { setAgencyUsers([]); }
      }
    } catch (e) { console.error(e); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showArchived]);

  const openSignLink = (d) => {
    if (!d.sign_url) { toast.error("No signing link on this agreement yet"); return; }
    window.open(d.sign_url, "_blank", "noopener,noreferrer");
  };
  const archiveDealer = async (d) => {
    try {
      await api.post(`/dealers/${d.id}/archive`);
      toast.success(`Archived ${d.dealer_name}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Archive failed"); }
  };
  const unarchiveDealer = async (d) => {
    try {
      await api.post(`/dealers/${d.id}/unarchive`);
      toast.success(`Restored ${d.dealer_name}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Restore failed"); }
  };
  const deleteDealer = async (d) => {
    if (!window.confirm(`Delete "${d.dealer_name}"? This cannot be undone.\n\nIf you might need it later, use Archive instead.`)) return;
    try {
      await api.delete(`/dealers/${d.id}`);
      toast.success(`Deleted ${d.dealer_name}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  // ------- Bulk selection + bulk actions -------
  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAll = () => setSelectedIds(new Set(dealers.map((d) => d.id)));
  const runBulk = async (action) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${ids.length} dealer(s)? This cannot be undone.\n\nExecuted agreements will be skipped.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/dealers/bulk-${action}`, { ids });
      const skipped = data.skipped?.length || 0;
      toast.success(`${action === "archive" ? "Archived" : action === "unarchive" ? "Restored" : "Deleted"} ${data.ok} dealer(s)${skipped ? ` · ${skipped} skipped` : ""}`);
      clearSelection();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
    finally { setBulkBusy(false); }
  };

  const isAdmin = role === "admin";
  const canAssign = role === "admin" || role === "agency_admin";

  const createDealer = async (e) => {
    e.preventDefault();
    if (!newDealer.dealer_name.trim() || !newDealer.contact_email.trim()) {
      toast.error("Dealer name and contact email are required");
      return;
    }
    setCreating(true);
    try {
      await api.post("/dealers", newDealer);
      toast.success("Dealer created");
      setNewDealer({ dealer_name: "", contact_name: "", contact_email: "", contact_phone: "" });
      await load();
      // Note: we intentionally do NOT auto-open the Send-packet modal — the
      // admin/agency-admin usually wants to assign agents first.
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  const openSendDialog = (dealer) => {
    const preselected = dealer?.form_ids?.length ? new Set(dealer.form_ids) : new Set(forms.map((f) => f.id));
    setSendDialog({ open: true, dealer, selected: preselected, sending: false, providerFilter: "all" });
  };

  const toggleForm = (id) => {
    setSendDialog((prev) => {
      const next = new Set(prev.selected);
      if (next.has(id)) next.delete(id); else next.add(id);
      return { ...prev, selected: next };
    });
  };

  const confirmSend = async () => {
    const { dealer, selected } = sendDialog;
    if (!dealer) return;
    if (selected.size === 0) { toast.error("Pick at least one form"); return; }
    setSendDialog((p) => ({ ...p, sending: true }));
    try {
      await api.post(`/dealers/${dealer.id}/send-agreement`, { form_ids: Array.from(selected) });
      toast.success(`Packet emailed to ${dealer.contact_email}`);
      setSendDialog({ open: false, dealer: null, selected: new Set(), sending: false, providerFilter: "all" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
      setSendDialog((p) => ({ ...p, sending: false }));
    }
  };

  const openAssignDialog = (dealer) => {
    const selected = new Set(dealer.assigned_agent_ids || []);
    setAssignDialog({ open: true, dealer, selected, saving: false });
  };
  const toggleAssign = (agentId) => {
    setAssignDialog((prev) => {
      const next = new Set(prev.selected);
      if (next.has(agentId)) next.delete(agentId); else next.add(agentId);
      return { ...prev, selected: next };
    });
  };
  const saveAssignments = async () => {
    const { dealer, selected } = assignDialog;
    if (!dealer) return;
    setAssignDialog((p) => ({ ...p, saving: true }));
    try {
      await api.put(`/dealers/${dealer.id}/assignments`, { assigned_agent_ids: Array.from(selected) });
      toast.success("Assignments updated");
      setAssignDialog({ open: false, dealer: null, selected: new Set(), saving: false });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
      setAssignDialog((p) => ({ ...p, saving: false }));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="dealers-list-page">
        <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Onboarding</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] mt-2 tracking-tight leading-none">
              Dealer signup
            </h1>
            <p className="text-sm text-[#6C757D] mt-3 max-w-2xl">
              Build a signing packet from the {forms.length} form(s) in the library and email it to any dealer. Once every form is signed, super admin countersigns to execute.
            </p>
          </div>
          {isAdmin && (
            <Link to="/admin/dealer-forms">
              <Button variant="outline" className="h-10 rounded-sm border-[#0B132B]/20" data-testid="manage-forms-link">
                <Settings2 size={14} className="mr-2"/> Manage forms library ({forms.length})
              </Button>
            </Link>
          )}
        </div>

        {/* No forms warning (both admin + agents) */}
        {forms.length === 0 && (
          <Card className="p-5 mb-6 border-2 border-dashed border-[#D4AF37] rounded-sm bg-[#D4AF37]/5 flex items-center gap-4" data-testid="no-forms-warning">
            <FileText size={22} className="text-[#D4AF37] shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-[#0B132B]">The forms library is empty</div>
              <div className="text-xs text-[#6C757D] mt-0.5">
                {isAdmin
                  ? "Upload at least one fillable dealer form so agents can start sending signing packets."
                  : "A super admin needs to upload the dealer form(s) before you can send a signing packet."}
              </div>
            </div>
            {isAdmin && (
              <Link to="/admin/dealer-forms">
                <Button className="h-10 rounded-sm bg-[#0B132B] text-white" data-testid="upload-first-form-btn">Upload forms</Button>
              </Link>
            )}
          </Card>
        )}

        {/* Quick-add dealer */}
        <Card className="p-6 mb-8 border-[#E9ECEF] rounded-sm" data-testid="dealer-create-card">
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-[#E9ECEF]">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Quick add</div>
              <div className="text-[11px] text-[#6C757D] mt-0.5">Just a name + email — you can fill locations and ACH later</div>
            </div>
            <div className="flex gap-2">
              <Link to="/dealers/add-existing" data-testid="dealer-add-existing-link">
                <Button variant="outline" className="h-10 rounded-sm border-[#0B132B]/20 text-[#0B132B] font-semibold">+ Add existing dealer</Button>
              </Link>
              <Link to="/dealers/new" data-testid="dealer-full-intake-link">
                <Button variant="outline" className="h-10 rounded-sm border-[#D4AF37] text-[#0B132B] hover:bg-[#D4AF37]/10 font-semibold">
                  Full intake (locations + ACH) →
                </Button>
              </Link>
            </div>
          </div>
          <form onSubmit={createDealer} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Dealership</Label>
              <Input value={newDealer.dealer_name} onChange={(e) => setNewDealer({...newDealer, dealer_name: e.target.value})}
                placeholder="e.g. Cannon Honda of Ponca City" className="mt-1 h-10 rounded-sm" data-testid="dealer-name-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Contact name</Label>
              <Input value={newDealer.contact_name} onChange={(e) => setNewDealer({...newDealer, contact_name: e.target.value})}
                placeholder="e.g. Jane Smith" className="mt-1 h-10 rounded-sm" data-testid="dealer-contact-name-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Contact email <span className="text-red-600">*</span></Label>
              <Input type="email" required value={newDealer.contact_email} onChange={(e) => setNewDealer({...newDealer, contact_email: e.target.value})}
                placeholder="jane@dealership.com" className="mt-1 h-10 rounded-sm" data-testid="dealer-contact-email-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Phone</Label>
              <Input value={newDealer.contact_phone} onChange={(e) => setNewDealer({...newDealer, contact_phone: e.target.value})}
                placeholder="555-…" className="mt-1 h-10 rounded-sm" data-testid="dealer-contact-phone-input" />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button type="submit" disabled={creating}
                className="h-11 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold px-6"
                data-testid="dealer-create-button">
                <Plus size={16} className="mr-2" /> {creating ? "Creating…" : "Add dealer"}
              </Button>
            </div>
          </form>
        </Card>

        {/* Dealers list */}
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]">Prospective &amp; active dealers</div>
            <div className="flex items-center gap-3">
              {dealers.length > 0 && (
                <button
                  type="button"
                  onClick={() => selectedIds.size === dealers.length ? clearSelection() : selectAll()}
                  className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] hover:text-[#0B132B]"
                  data-testid="dealer-select-all">
                  {selectedIds.size === dealers.length ? "Deselect all" : "Select all"}
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] hover:text-[#0B132B] transition-colors"
                data-testid="toggle-show-archived"
              >
                {showArchived ? <><EyeOff size={12}/> Hide archived</> : <><Eye size={12}/> Show archived</>}
              </button>
            </div>
          </div>

          {/* Bulk action toolbar */}
          {selectedIds.size > 0 && (
            <Card className="p-3 border-[#D4AF37] bg-[#D4AF37]/10 rounded-sm flex flex-wrap items-center gap-3" data-testid="dealer-bulk-toolbar">
              <div className="text-sm font-bold text-[#0B132B] flex-1">
                {selectedIds.size} dealer(s) selected
              </div>
              <Button size="sm" variant="outline" onClick={() => runBulk("archive")} disabled={bulkBusy}
                className="h-8 rounded-sm border-[#0B132B]/20 text-[#0B132B] bg-white" data-testid="dealer-bulk-archive">
                <Archive size={12} className="mr-1"/> Archive
              </Button>
              {showArchived && (
                <Button size="sm" variant="outline" onClick={() => runBulk("unarchive")} disabled={bulkBusy}
                  className="h-8 rounded-sm border-[#0B132B]/20 text-[#0B132B] bg-white" data-testid="dealer-bulk-unarchive">
                  <ArchiveRestore size={12} className="mr-1"/> Restore
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => runBulk("delete")} disabled={bulkBusy}
                className="h-8 rounded-sm border-red-200 text-red-600 hover:bg-red-50 bg-white" data-testid="dealer-bulk-delete">
                <Trash2 size={12} className="mr-1"/> Delete
              </Button>
              <button type="button" onClick={clearSelection} className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D] hover:text-[#0B132B]" data-testid="dealer-bulk-clear">
                Clear
              </button>
            </Card>
          )}
          {dealers.length === 0 ? (
            <Card className="p-10 border-dashed border-2 border-[#E9ECEF] rounded-sm text-center bg-white">
              <Handshake size={28} className="mx-auto text-[#6C757D]" />
              <div className="mt-2 text-sm text-[#6C757D]">No dealers yet. Add one above to get started.</div>
            </Card>
          ) : (
            dealers.map((d) => {
              const st = STATUS_META[d.status] || STATUS_META.draft;
              const packetSize = d.form_ids?.length || 0;
              return (
                <Card key={d.id} className={`p-5 border-[#E9ECEF] rounded-sm flex flex-wrap items-center gap-4 ${d.archived ? "opacity-70 bg-[#F8F9FA]" : ""} ${selectedIds.has(d.id) ? "ring-2 ring-[#D4AF37]" : ""}`}
                  data-testid={`dealer-row-${d.id}`}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(d.id)}
                    onChange={() => toggleRow(d.id)}
                    className="mt-0.5 shrink-0 cursor-pointer"
                    data-testid={`dealer-select-${d.id}`}
                    aria-label={`Select ${d.dealer_name}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="text-base font-bold text-[#0B132B]">{d.dealer_name}</div>
                      <span className={`inline-flex items-center px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] font-bold border rounded-sm ${st.tone}`}>{st.label}</span>
                      {d.archived && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] font-bold text-[#6C757D] bg-white border border-[#E9ECEF] px-2 py-0.5 rounded-sm" data-testid={`dealer-archived-badge-${d.id}`}>
                          <Archive size={10}/> Archived
                        </span>
                      )}
                      {packetSize > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-[#0B132B] bg-[#F8F9FA] border border-[#E9ECEF] px-2 py-0.5 rounded-sm">
                          <Files size={11}/> {packetSize} form(s)
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#6C757D] mt-1 truncate">
                      {d.contact_name ? `${d.contact_name} · ` : ""}{d.contact_email} · Created {fmt(d.created_at)}
                    </div>
                    {d.signed_at && <div className="text-xs text-green-700 mt-0.5">Dealer signed {fmt(d.signed_at)}</div>}
                    {d.countersigned_at && <div className="text-xs text-green-700 mt-0.5">Executed {fmt(d.countersigned_at)}</div>}
                    {d.archived_at && <div className="text-xs text-[#6C757D] mt-0.5">Archived {fmt(d.archived_at)}</div>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {d.sign_url && (
                      <Button onClick={() => openSignLink(d)} size="sm" variant="outline"
                        className="h-9 rounded-sm border-[#0B132B]/20 text-[#0B132B]"
                        data-testid={`dealer-open-signlink-${d.id}`}
                        title="Open the dealer signing page in a new tab">
                        <ExternalLink size={13} className="mr-1.5" /> Open agreement
                      </Button>
                    )}
                    {!d.archived && (d.status === "draft" || d.status === "sent") && forms.length > 0 && (
                      <Button onClick={() => openSendDialog(d)} size="sm"
                        className="h-9 rounded-sm bg-[#D4AF37] hover:bg-[#B8942F] text-[#0B132B] font-bold"
                        data-testid={`dealer-send-${d.id}`}>
                        <Send size={13} className="mr-1.5" /> {d.status === "sent" ? "Resend" : "Send for e-sign"}
                      </Button>
                    )}
                    {!d.archived && canAssign && (
                      <Button onClick={() => openAssignDialog(d)} size="sm" variant="outline"
                        className="h-9 rounded-sm border-[#0B132B]/20 text-[#0B132B]"
                        data-testid={`dealer-assign-${d.id}`}>
                        <UserPlus size={13} className="mr-1.5" />
                        {d.assigned_agent_ids?.length ? `${d.assigned_agent_ids.length} agent(s)` : "Assign agents"}
                      </Button>
                    )}
                    {!d.archived && d.status === "dealer_signed" && isAdmin && (
                      <Link to={`/dealers/${d.id}`}>
                        <Button size="sm" className="h-9 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-bold" data-testid={`dealer-countersign-${d.id}`}>
                          <Clock size={13} className="mr-1.5" /> Countersign
                        </Button>
                      </Link>
                    )}
                    <Link to={`/dealers/${d.id}/profile`}>
                      <Button size="sm" className="h-9 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid={`dealer-profile-${d.id}`}>
                        Profile
                      </Button>
                    </Link>
                    <Link to={`/dealers/${d.id}`}>
                      <Button size="sm" variant="outline" className="h-9 rounded-sm border-[#0B132B]/20" data-testid={`dealer-open-${d.id}`}>
                        Agreement
                      </Button>
                    </Link>
                    {d.archived ? (
                      <Button onClick={() => unarchiveDealer(d)} size="sm" variant="outline"
                        className="h-9 rounded-sm border-[#0B132B]/20 text-[#0B132B]"
                        data-testid={`dealer-unarchive-${d.id}`}
                        title="Restore this dealer so it re-appears on the main list">
                        <ArchiveRestore size={13} className="mr-1.5" /> Restore
                      </Button>
                    ) : (
                      <Button onClick={() => archiveDealer(d)} size="sm" variant="outline"
                        className="h-9 rounded-sm border-[#0B132B]/20 text-[#6C757D] hover:text-[#0B132B]"
                        data-testid={`dealer-archive-${d.id}`}
                        title="Hide from the main list without deleting">
                        <Archive size={13} className="mr-1.5" /> Archive
                      </Button>
                    )}
                    <Button onClick={() => deleteDealer(d)} size="sm" variant="outline"
                      className="h-9 rounded-sm border-red-200 text-red-600 hover:bg-red-50"
                      data-testid={`dealer-delete-${d.id}`}
                      title="Permanently delete — cannot be undone">
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      </main>

      {/* Send packet dialog */}
      <Dialog open={sendDialog.open} onOpenChange={(o) => !o && setSendDialog({ open: false, dealer: null, selected: new Set(), sending: false, providerFilter: "all" })}>
        <DialogContent className="max-w-lg" data-testid="send-packet-dialog">
          <DialogHeader>
            <DialogTitle>Send signing packet</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#6C757D]">
            Pick which forms {sendDialog.dealer?.contact_name || sendDialog.dealer?.contact_email} needs to sign.
          </div>
          {(() => {
            const providers = Array.from(new Set(forms.map((f) => f.provider || "Advantage Warranty Group"))).sort();
            const filter = sendDialog.providerFilter || "all";
            const visible = filter === "all" ? forms : forms.filter((f) => (f.provider || "Advantage Warranty Group") === filter);
            return (
              <>
                {providers.length > 1 && (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid="packet-provider-filter">
                    {["all", ...providers].map((p) => {
                      const on = filter === p;
                      const count = p === "all" ? forms.length : forms.filter((x) => (x.provider || "Advantage Warranty Group") === p).length;
                      return (
                        <button
                          key={p}
                          onClick={() => setSendDialog({ ...sendDialog, providerFilter: p })}
                          className={`text-[10px] uppercase tracking-[0.14em] font-bold px-2.5 py-1 rounded-sm border transition-colors ${on ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#0B132B]" : "border-[#E9ECEF] bg-white text-[#6C757D] hover:border-[#0B132B]/30"}`}
                          data-testid={`packet-provider-chip-${p}`}
                        >
                          {p === "all" ? "All" : p} · {count}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="mt-4 space-y-1 max-h-[320px] overflow-y-auto">
                  {visible.map((f) => {
                    const checked = sendDialog.selected.has(f.id);
                    return (
                      <label key={f.id} className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${checked ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#E9ECEF] bg-white hover:bg-[#F8F9FA]"}`}
                        data-testid={`packet-form-${f.id}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleForm(f.id)} className="mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-sm font-bold text-[#0B132B] truncate">{f.name}</div>
                            {f.provider && f.provider !== "Advantage Warranty Group" && (
                              <span className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40 px-1.5 py-0.5 rounded-sm">{f.provider}</span>
                            )}
                          </div>
                          <div className="text-[11px] text-[#6C757D] truncate">{f.filename} · {f.fields_count} field(s) mapped</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </>
            );
          })()}
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setSendDialog({ open: false, dealer: null, selected: new Set(), sending: false, providerFilter: "all" })} className="rounded-sm" data-testid="packet-cancel">Cancel</Button>
            <Button onClick={confirmSend} disabled={sendDialog.sending || sendDialog.selected.size === 0}
              className="rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="packet-send">
              <Send size={13} className="mr-2" /> {sendDialog.sending ? "Sending…" : `Send ${sendDialog.selected.size} form(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Assign agents dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(o) => !o && setAssignDialog({ open: false, dealer: null, selected: new Set(), saving: false })}>
        <DialogContent className="max-w-lg" data-testid="assign-agents-dialog">
          <DialogHeader>
            <DialogTitle>Assign agents to {assignDialog.dealer?.dealer_name}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#6C757D]">
            Pick the agents in your agency who should see and manage this dealer. Agents set to "assigned-only" will only see dealers you tick here.
          </div>
          <div className="mt-4 space-y-1 max-h-[320px] overflow-y-auto border border-[#E9ECEF] rounded-sm">
            {agencyUsers.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#6C757D] italic">No agents in your agency yet.</div>
            ) : agencyUsers.map((u) => {
              const checked = assignDialog.selected.has(u.id);
              return (
                <label key={u.id} className={`flex items-start gap-3 p-3 border-b border-[#E9ECEF] cursor-pointer transition-colors ${checked ? "bg-[#D4AF37]/5" : "hover:bg-[#F8F9FA]"}`}
                  data-testid={`assign-agent-${u.id}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleAssign(u.id)} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-[#0B132B] truncate">{u.name} <span className="text-[10px] text-[#6C757D] font-normal">({u.role})</span></div>
                    <div className="text-[11px] text-[#6C757D] truncate">{u.email}</div>
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setAssignDialog({ open: false, dealer: null, selected: new Set(), saving: false })} className="rounded-sm">Cancel</Button>
            <Button onClick={saveAssignments} disabled={assignDialog.saving} className="rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="assign-save-btn">
              <UsersIcon size={13} className="mr-2" /> {assignDialog.saving ? "Saving…" : `Save (${assignDialog.selected.size})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
